/**
 * One carried row said round by round under its name (`DecisionAttribution.carriedByRound`, OR-27.11): "changed to Hold in FY2026 (+$0.55), held in FY2027 (no change), reverted to Hold in FY2028 (−$0.55)".
 * A leg is the round a setting changed in; rounds with no leg since the first one are "held". The pooled leg (the one with `fromRound`) covers rounds `fromRound` to its round.
 * The words come from consecutive legs' values: a number that rose is "raised to", one that fell "lowered to", anything else "changed to", and a value that returns to the one before is "reverted to".
 * Pure view of the report's legs.
 */
import type { ContentCatalog, RoundReport } from "@bsim/contracts";
import { fiscalYearLabel, formatUsdPerShare } from "../format";
import { optionLabel, type SettingValue } from "../decisions/decisionValues";

export interface CarriedLeg { readonly round: number; readonly fromRound?: number | undefined; readonly value?: unknown; readonly valueCreatedUsdPerShare?: number | undefined }

/** How a year is named when the caller has no catalog: "round 2". Callers with a catalog pass `fiscalYearLabel`. */
const roundName = (n: number): string => `round ${n}`;
const rangeText = (name: (n: number) => string, from: number, to: number): string => (from === to ? name(from) : `${name(from)} to ${name(to)}`);
const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** The legs of one path said in a line (the row above it names the decision), or null when it has fewer than two legs. `currentRound` is the round of the report (earlier rounds are 1…currentRound−1); `valueText` names a setting in the player's words. */
export function carriedLegsLine(legs: readonly CarriedLeg[] | undefined, currentRound: number, valueText: (value: unknown) => string = String, yearName: (roundNo: number) => string = roundName): string | null {
  if (legs === undefined || legs.length < 2) return null;
  const parts: string[] = [];
  let next = 0; // the last round already said
  legs.forEach((leg, i) => {
    if (i > 0 && leg.round > next + 1) parts.push(`held in ${rangeText(yearName, next + 1, leg.round - 1)} (no change)`);
    const before = legs[i - 1]?.value;
    const verb = leg.value === undefined ? "changed"
      : i >= 2 && same(legs[i - 2]?.value, leg.value) ? "reverted to"
      : typeof leg.value === "number" && typeof before === "number" ? `${leg.value > before ? "raised" : "lowered"} to`
      : "changed to";
    const where = leg.fromRound === undefined ? yearName(leg.round) : rangeText(yearName, leg.fromRound, leg.round);
    parts.push(`${verb}${leg.value === undefined ? "" : ` ${valueText(leg.value)}`} in ${where} (${formatUsdPerShare(leg.valueCreatedUsdPerShare ?? 0, { signed: true })})`);
    next = leg.round;
  });
  if (next < currentRound - 1) parts.push(`held in ${rangeText(yearName, next + 1, currentRound - 1)} (no change)`);
  return parts.join(", ");
}

/** The legs line of one carried path of a report, with the setting named as the decision page names it; the one reader both Results screens use. */
export function carriedLegsOf(report: RoundReport, catalog: ContentCatalog, path: string): string | null {
  const entry = catalog.decisions.find((d) => path === d.decisionId || path.startsWith(`${d.decisionId}.`));
  const options = entry?.options[path.slice((entry?.decisionId.length ?? 0) + 1)];
  const valueText = (v: unknown): string => (options === undefined ? String(v) : optionLabel(options, v as SettingValue));
  return carriedLegsLine(report.decisionAttribution?.carriedByRound?.[path], report.roundNo, valueText, (n) => fiscalYearLabel(catalog, n));
}
